import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const BlockchainPrivacyControls = ({ blockchainSettings, onBlockchainSettingsChange }) => {
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  const privacyLevels = [
    { 
      value: 'maximum', 
      label: 'Maximum Privacy',
      description: 'Use privacy coins and advanced obfuscation techniques'
    },
    { 
      value: 'high', 
      label: 'High Privacy',
      description: 'Minimize transaction linkability and metadata exposure'
    },
    { 
      value: 'balanced', 
      label: 'Balanced',
      description: 'Standard privacy with good performance and compatibility'
    },
    { 
      value: 'transparent', 
      label: 'Transparent',
      description: 'Full transparency for regulatory compliance'
    }
  ];

  const networkOptions = [
    { 
      value: 'ethereum-mainnet', 
      label: 'Ethereum Mainnet',
      description: 'Public Ethereum network with full transparency'
    },
    { 
      value: 'polygon', 
      label: 'Polygon',
      description: 'Layer 2 solution with lower fees and faster transactions'
    },
    { 
      value: 'private-consortium', 
      label: 'Private Consortium',
      description: 'Permissioned network for enhanced privacy'
    },
    { 
      value: 'hyperledger-fabric', 
      label: 'Hyperledger Fabric',
      description: 'Enterprise blockchain with channel-based privacy'
    }
  ];

  const metadataDisclosureOptions = [
    { value: 'minimal', label: 'Minimal - Only essential data' },
    { value: 'standard', label: 'Standard - Required metadata only' },
    { value: 'extended', label: 'Extended - Include additional context' },
    { value: 'full', label: 'Full - Complete transaction details' }
  ];

  const transactionPrivacyMethods = [
    {
      id: 'ring-signatures',
      name: 'Ring Signatures',
      description: 'Hide the true signer among a group of possible signers',
      enabled: blockchainSettings?.privacyMethods?.ringSignatures || false,
      compatibility: 'High',
      performanceImpact: 'Medium'
    },
    {
      id: 'stealth-addresses',
      name: 'Stealth Addresses',
      description: 'Generate unique addresses for each transaction to prevent linking',
      enabled: blockchainSettings?.privacyMethods?.stealthAddresses || false,
      compatibility: 'Medium',
      performanceImpact: 'Low'
    },
    {
      id: 'zero-knowledge-proofs',
      name: 'Zero-Knowledge Proofs',
      description: 'Prove transaction validity without revealing details',
      enabled: blockchainSettings?.privacyMethods?.zkProofs || true,
      compatibility: 'Medium',
      performanceImpact: 'High'
    },
    {
      id: 'confidential-transactions',
      name: 'Confidential Transactions',
      description: 'Hide transaction amounts while maintaining verifiability',
      enabled: blockchainSettings?.privacyMethods?.confidentialTx || false,
      compatibility: 'Low',
      performanceImpact: 'High'
    }
  ];

  const handleSettingChange = (key, value) => {
    const updatedSettings = {
      ...blockchainSettings,
      [key]: value
    };
    onBlockchainSettingsChange(updatedSettings);
  };

  const handlePrivacyMethodToggle = (methodId, enabled) => {
    const updatedSettings = {
      ...blockchainSettings,
      privacyMethods: {
        ...blockchainSettings?.privacyMethods,
        [methodId?.replace('-', '')]: enabled
      }
    };
    onBlockchainSettingsChange(updatedSettings);
  };

  const handleAdvancedSettingChange = (key, value) => {
    const updatedSettings = {
      ...blockchainSettings,
      advanced: {
        ...blockchainSettings?.advanced,
        [key]: value
      }
    };
    onBlockchainSettingsChange(updatedSettings);
  };

  const getPrivacyLevelColor = (level) => {
    switch (level) {
      case 'maximum': return 'text-success';
      case 'high': return 'text-success';
      case 'balanced': return 'text-warning';
      case 'transparent': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getPrivacyLevelIcon = (level) => {
    switch (level) {
      case 'maximum': return 'ShieldCheck';
      case 'high': return 'Shield';
      case 'balanced': return 'ShieldAlert';
      case 'transparent': return 'Eye';
      default: return 'Shield';
    }
  };

  const getCompatibilityColor = (compatibility) => {
    switch (compatibility) {
      case 'High': return 'text-success';
      case 'Medium': return 'text-warning';
      case 'Low': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getPerformanceColor = (impact) => {
    switch (impact) {
      case 'Low': return 'text-success';
      case 'Medium': return 'text-warning';
      case 'High': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Blockchain Privacy Controls</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Configure transaction privacy levels and metadata disclosure preferences
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Link" size={20} className="text-primary" />
          <span className="text-sm font-medium text-primary">Blockchain Protected</span>
        </div>
      </div>
      <div className="space-y-6">
        {/* Privacy Level */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">Transaction Privacy Level</label>
          <Select
            options={privacyLevels}
            value={blockchainSettings?.privacyLevel || 'balanced'}
            onChange={(value) => handleSettingChange('privacyLevel', value)}
            placeholder="Select privacy level"
          />
          <div className="flex items-center space-x-2 text-sm">
            <Icon 
              name={getPrivacyLevelIcon(blockchainSettings?.privacyLevel || 'balanced')} 
              size={16} 
              className={getPrivacyLevelColor(blockchainSettings?.privacyLevel || 'balanced')} 
            />
            <span className="text-muted-foreground">
              {privacyLevels?.find(l => l?.value === (blockchainSettings?.privacyLevel || 'balanced'))?.description}
            </span>
          </div>
        </div>

        {/* Preferred Network */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">Preferred Blockchain Network</label>
          <Select
            options={networkOptions}
            value={blockchainSettings?.preferredNetwork || 'ethereum-mainnet'}
            onChange={(value) => handleSettingChange('preferredNetwork', value)}
            placeholder="Select blockchain network"
          />
          <div className="text-sm text-muted-foreground">
            {networkOptions?.find(n => n?.value === (blockchainSettings?.preferredNetwork || 'ethereum-mainnet'))?.description}
          </div>
        </div>

        {/* Metadata Disclosure */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">Metadata Disclosure Level</label>
          <Select
            options={metadataDisclosureOptions}
            value={blockchainSettings?.metadataDisclosure || 'standard'}
            onChange={(value) => handleSettingChange('metadataDisclosure', value)}
            placeholder="Select metadata level"
          />
        </div>

        {/* Privacy Methods */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground">Privacy Enhancement Methods</h4>
          <div className="space-y-3">
            {transactionPrivacyMethods?.map((method) => (
              <div key={method?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      checked={method?.enabled}
                      onChange={(e) => handlePrivacyMethodToggle(method?.id, e?.target?.checked)}
                    />
                    <div className="flex-1">
                      <h5 className="font-medium text-sm text-foreground">{method?.name}</h5>
                      <p className="text-xs text-muted-foreground mt-1">{method?.description}</p>
                    </div>
                  </div>
                </div>
                
                <div className="ml-7 flex items-center space-x-4 text-xs">
                  <div className="flex items-center space-x-1">
                    <span className="text-muted-foreground">Compatibility:</span>
                    <span className={getCompatibilityColor(method?.compatibility)}>
                      {method?.compatibility}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className="text-muted-foreground">Performance Impact:</span>
                    <span className={getPerformanceColor(method?.performanceImpact)}>
                      {method?.performanceImpact}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Basic Privacy Options */}
        <div className="space-y-3">
          <h4 className="font-medium text-foreground">Privacy Options</h4>
          <div className="space-y-3">
            <Checkbox
              label="Minimize on-chain footprint"
              description="Store minimal data on blockchain, use off-chain storage for details"
              checked={blockchainSettings?.minimizeFootprint || true}
              onChange={(e) => handleSettingChange('minimizeFootprint', e?.target?.checked)}
            />
            
            <Checkbox
              label="Use privacy-focused gas strategies"
              description="Optimize transaction timing and batching for enhanced privacy"
              checked={blockchainSettings?.privacyGasStrategy || false}
              onChange={(e) => handleSettingChange('privacyGasStrategy', e?.target?.checked)}
            />
            
            <Checkbox
              label="Enable transaction mixing"
              description="Mix transactions with others to obscure transaction patterns"
              checked={blockchainSettings?.enableMixing || false}
              onChange={(e) => handleSettingChange('enableMixing', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Advanced Settings Toggle */}
        <div className="border-t border-border pt-4">
          <Button
            variant="ghost"
            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
            iconName={showAdvancedSettings ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            className="w-full justify-between"
          >
            Advanced Blockchain Settings
          </Button>
        </div>

        {/* Advanced Settings */}
        {showAdvancedSettings && (
          <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">Transaction Confirmation Threshold</label>
              <Select
                options={[
                  { value: '1', label: '1 confirmation - Fast, less secure' },
                  { value: '3', label: '3 confirmations - Balanced' },
                  { value: '6', label: '6 confirmations - Secure' },
                  { value: '12', label: '12 confirmations - Maximum security' }
                ]}
                value={blockchainSettings?.advanced?.confirmationThreshold || '6'}
                onChange={(value) => handleAdvancedSettingChange('confirmationThreshold', value)}
              />
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">Gas Price Strategy</label>
              <Select
                options={[
                  { value: 'privacy', label: 'Privacy Optimized - Variable timing' },
                  { value: 'standard', label: 'Standard - Market rates' },
                  { value: 'fast', label: 'Fast - Higher fees for speed' }
                ]}
                value={blockchainSettings?.advanced?.gasPriceStrategy || 'standard'}
                onChange={(value) => handleAdvancedSettingChange('gasPriceStrategy', value)}
              />
            </div>

            <Checkbox
              label="Enable decoy transactions"
              description="Generate additional transactions to obscure real activity patterns"
              checked={blockchainSettings?.advanced?.enableDecoys || false}
              onChange={(e) => handleAdvancedSettingChange('enableDecoys', e?.target?.checked)}
            />

            <Checkbox
              label="Use Tor for blockchain communication"
              description="Route blockchain network traffic through Tor for IP address privacy"
              checked={blockchainSettings?.advanced?.useTor || false}
              onChange={(e) => handleAdvancedSettingChange('useTor', e?.target?.checked)}
            />

            <Checkbox
              label="Enable cross-chain privacy bridges"
              description="Use privacy-preserving bridges when moving assets between chains"
              checked={blockchainSettings?.advanced?.privacyBridges || false}
              onChange={(e) => handleAdvancedSettingChange('privacyBridges', e?.target?.checked)}
            />
          </div>
        )}
      </div>
      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div className="text-sm">
            <p className="text-foreground font-medium">Blockchain Privacy Trade-offs</p>
            <p className="text-muted-foreground mt-1">
              Enhanced privacy features may impact transaction speed, cost, and compatibility with some services. 
              Choose settings that balance your privacy needs with practical usability requirements.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockchainPrivacyControls;